/* eslint-disable object-curly-spacing */ 
/* eslint-disable no-undef */ 
/* eslint-disable own-handles */ 
/* eslint-disable no-innerhtml-assign */ 
/* eslint-disable consistent-method-names */ 
/* eslint-disable no-jsdoc */ 
/* eslint-disable max-len */
/**
 * Provides current traffic incidents in given region, on given zoom level with given style using the TomTom's
 * [Traffic Incidents Details API](https://developer.tomtom.com/online-traffic/online-traffic-documentation-online-traffic-incidents/traffic-incident-details)
 *
 * The current road situation is updated every minute. Please use the
 * {{#crossLink "TrafficModelId"}}{{/crossLink}} service in conjunction with your request to get traffic
 * incidents aligned with other services.
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "TrafficIncidents/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "TrafficIncidents/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * The final response from this  service is a GeoJSON object.
 *
 * @class TrafficIncidents
 * @uses Chain
 * @module Services
 * @uses KeyMixin
 * @uses BoundingBoxMixin
 * @uses ProtocolMixin
 * @constructor
 * @param {Object} [options]
 * @param {String} [options.key] An API Key issued from <a href="http://developer.tomtom.com">Developer Portal</a>
 * @param {Number} [options.zoomLevel] A zoom level of the current map view `<0, 18>`. This option **is required**.
 * @param {Object|Array|String} [options.boundingBox] The area to extract incidents from. Conversion objects
 *     are described in the {{#crossLink "TrafficIncidents/boundingBox:method"}}{{/crossLink}} function.
 * @param {String} [options.style] Style name, **needs to be** one of `"s1"`, `"s2"`, `"s3"`, `"night"`.
 *     This needs to match the style of your raster traffic tiles to match traffic tubes with traffic icons.
 * @param {String} [options.language="en"] ISO 639-1 language code of the incidentCause and description
 * @param {Number} [options.trafficModelID=-1] Valid, not older than two minutes, traffic model ID.
 * @param {Boolean} [options.expandCluster=false] Should service include details of incidents that are
 *     clustered
 * @param {Boolean} [options.originalPosition=false] Should service include original incidents' position
 * @param {"http"|"https"} [options.protocol] Protocol's type
 * @param {Boolean} [options.preserveCluster=false] Determines if clusters should be preserved
 *     when requesting for details of all the incidents
 *
 * @example
 * ```javascript
 * function callbackFn(response) {
 *   console.log(response);
 * }
 * tomtom.trafficIncidents()
 *   .key("<Your API key>")
 *   .boundingBox("0,0,1,1")
 *   .style("s1")
 *   .zoomLevel(12)
 *   .go(callbackFn);
 * ```
 */
/* eslint-enable max-len */
/* jshint nomen:false */
define(['rest/trafficIncidents',
    'model/traffic/trafficIncidents',
    './chain',
    './validators',
    './converters',
    './worldEdge',
    'common/parameterApplications'],
function(trafficIncidents, model, chain, validators, converters, worldEdge, parameterApplications) {
    'use strict';

    var fields = {
        key: {validator: validators.string},

        /**
         * Sets or returns the value of the `zoomLevel` option.
         *
         * The zoom level of currently displayed map.
         * It is needed as the style (visibility, clustering, etc...) depends on current zoom level.
         * @method zoomLevel
         * @chainable
         * @param {Number} [newValue] Zoom level from `0` to `18`
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {Number} The same service instance or the current option value if no argument was given
         */
        zoomLevel: {validator: validators.zoomLevel, required: true, application: parameterApplications.PATH,
                    name: 'zoom'},
        boundingBox: {converter: converters.boundingBox, validator: validators.boundingBox, required: true},

        /**
         * Sets or returns the value of the `style` option.
         *
         * The style that will be used to render the
         * traffic tile in the Maps API. This will have an effect on the coordinates of traffic incidents in the
         * reply.
         *
         * @method style
         * @chainable
         * @param {String} [newValue] Style name, needs to be on of _'s1', 's2', 's3', 'night'_. This needs to match
         *     style of your raster traffic tiles to match traffic tubes with traffic icons.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
        style: {validator: validators.oneOfValue(['s1', 's2', 's3', 'night'], 'traffic style'), required: true,
                application: parameterApplications.PATH},

        /**
         * Sets or returns the value of the `language` option.
         *
         * Expects an ISO 639-1 language code for the `incidentCause` and `description` fields in the response.
         * English is the default language.
         *
         * The supported languages are: `ca`, `cs`, `da`, `de`, `en`, `es`, `fi`, `fr`, `hu`,
         * `it`, `nl`, `no`, `pl`, `pt`, `sk`, `sv`, `tr`.
         *
         * There is a conversion mechanism in place that will (ignoring the case) try to match the user input
         * with a valid language.
         *
         * For example: for the French language the following options are possible: "fr", "fre", "fr-fr", "fr_fr",
         * "french".
         *
         * @method language
         * @chainable
         * @param {String} [newValue] Language code, a conversion mechanism will be applied.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
        language: {converter: converters.trafficIncidentsLanguage, validator: validators.trafficIncidentsLanguage},

        /**
         * Sets or returns the value of the `trafficModelID` option.
         *
         * Number referencing the traffic model to use.
         * This can be obtained from the the {{#crossLink "TrafficModelId"}}{{/crossLink}} service.
         *
         * It is updated every minute, and is valid for two minutes before it times out.
         *
         * If a wrong Traffic Model ID is specified, the correct one will be returned by the interface.
         *
         * The default value of `-1` will always invoke the most recent traffic model. Nevertheless it is
         * a good practice to use the value obtained from the service as this will guarantee an alignment
         * with the raster traffic layer.
         *
         * @method trafficModelID
         * @chainable
         * @param {Number} [newValue] Valid, not older than two minutes, traffic model ID.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {Number} The same service instance or the current option value if no argument was given
         */
        trafficModelID: {validator: validators.integer, defaultValue: -1, application: parameterApplications.PATH},

        geometries: {validator: validators.oneOfValue(['shifted', 'original'], 'traffic geometries')},

        /**
         * Sets or returns the value of the `expandCluster` option.
         *
         * Tells the service to list all traffic incidents in a cluster separately.
         *
         * When is enabled the clusters in the response will be represented as geometry collections
         * and the cluster coordinates will appear as clusterCoordinates parameters.
         *
         * Enabling this feature is useful when the user wants to obtain details of the incidents
         * that are being clustered or wants to perform a client-side clustering.
         *
         * @method expandCluster
         * @chainable
         * @param {Boolean} [newValue] Should service include details of incidents that are clustered
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {Boolean} The same service instance or the current option value if no argument was given
         */
        expandCluster: {validator: validators.bool, defaultValue: false},

        /**
         * Sets or returns the value of the `preserveCluster` option.
         *
         * Tells the service to keep all traffic incidents clustered when receiving a list of all traffic
         * incidents in a cluster separately.
         *
         * This allows to get all the incidents details while remaining in clusters.
         *
         * @method preserveCluster
         * @chainable
         * @param {Boolean} [newValue] Should service keep clusters data
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {Boolean} The same service instance or the current option value if no argument was given
         */
        preserveCluster: {validator: validators.bool, defaultValue: false},

        /**
         * Sets or returns the value of the `originalPosition` option.
         *
         * Return the original position of the incident (`originalPosition` attribute) as well as the one
         * shifted to the beginning of the traffic tube.
         *
         * @method originalPosition
         * @chainable
         * @param {Boolean} [newValue] Should service include original incidents' position
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {Boolean} The same service instance or the current option value if no argument was given
         */
        originalPosition: {validator: validators.bool, defaultValue: false},

        protocol: {validator: validators.oneOfValue(['http', 'https'], 'protocol')},

        projection: {
            defaultValue: 'EPSG4326'
        }
    };

    function addBoundingBoxParameters(data, boundingBox) {
        data.minLon = boundingBox.minLon;
        data.maxLon = boundingBox.maxLon;
        data.minLat = boundingBox.minLat;
        data.maxLat = boundingBox.maxLat;
        return data;
    }

    /*
     * At this moment it's impossible to map one field to two and more output fields but we need to do this, e.g.
     * bounding box needs to be mapped to topLeft and btmRight. That's why this method tries to fix this
     */
    function modifyFields(fields) {
        var pathField = {
            application: parameterApplications.PATH
        };
        var resultFields = Object.assign({}, fields);

        if (resultFields.boundingBox) {
            resultFields.minLon = pathField;
            resultFields.maxLon = pathField;
            resultFields.minLat = pathField;
            resultFields.maxLat = pathField;
            delete resultFields.boundingBox;
        }

        return resultFields;
    }

    function incidentsDetailsQuery(options, callback) {
        return trafficIncidents.incidentsDetails(modifyFields(fields), options, callback);
    }

    function buildHandleChain(preserveCluster) {
        return function(data, callback) {
            worldEdge(incidentsDetailsQuery, model(preserveCluster),
                data, callback, addBoundingBoxParameters);
        };
    }

    return function(options) {
        options = options || {};
        options.__serviceType = 'traffic';
        return chain(fields, buildHandleChain(options && options.preserveCluster), options);
    };
});



// WEBPACK FOOTER //
// ./services/trafficIncidents.js